import React, { useEffect, useRef, useState } from "react";
import {
  Animated,
  Easing,
  Image,
  ImageResizeMode,
  ImageSourcePropType,
  ImageStyle,
  Platform,
  Pressable,
  StyleProp,
  View,
  ViewStyle,
  useWindowDimensions,
} from "react-native";
import { LinearGradient } from "expo-linear-gradient";
import { useAppDispatch } from "../../hooks/store-hook";
import { lightBoxActions } from "../../store/lightBoxSlice";
import CustomCheckbox from "../common/CustomCheckbox";
import Label from "../common/Label";

export interface MissionPromotionPopupProps {
  title: string;
  subtitle: string;
  ctaLabel: string;
  dontShowLabel: string;
  checkboxValue: string;
  backgroundColor: string;
  backgroundGradientColors?: [string, string, ...string[]];
  backgroundDecorationColor?: string;
  buttonColor: string;
  buttonTextColor?: string;
  buttonBorderColor?: string;
  buttonShadowColor?: string;
  checkboxTextColor: string;
  checkboxBorderColor: string;
  checkboxBackgroundColor: string;
  icon?: React.ReactNode;
  iconContainerStyle?: StyleProp<ViewStyle>;
  iconContentLeftPadding?: number;
  borderColor?: string;
  shadowColor?: string;
  artSource?: ImageSourcePropType;
  artResizeMode?: ImageResizeMode;
  artWebStyle?: StyleProp<ImageStyle>;
  artNativeStyle?: StyleProp<ImageStyle>;
  contentMinHeight?: number;
  titleNumberOfLines?: number;
  subtitleNumberOfLines?: number;
  titleFontSize?: number;
  subtitleFontSize?: number;
  onClose?(dontShowAgain: boolean): void;
  onProceed?(dontShowAgain: boolean): void;
}

const useNativeAnimationDriver = Platform.OS !== "web";

const MissionPromotionPopup = (props: MissionPromotionPopupProps) => {
  const dispatch = useAppDispatch();
  const layout = useWindowDimensions();
  const [checked, setChecked] = useState(false);
  const checkedRef = useRef(false);
  const opacity = useRef(new Animated.Value(0)).current;
  const translateY = useRef(new Animated.Value(-12)).current;
  const closingRef = useRef(false);

  useEffect(() => {
    checkedRef.current = checked;
  }, [checked]);

  useEffect(() => {
    Animated.parallel([
      Animated.timing(opacity, {
        toValue: 1,
        duration: 220,
        easing: Easing.out(Easing.quad),
        useNativeDriver: useNativeAnimationDriver,
      }),
      Animated.timing(translateY, {
        toValue: 0,
        duration: 220,
        easing: Easing.out(Easing.quad),
        useNativeDriver: useNativeAnimationDriver,
      }),
    ]).start();
  }, [opacity, translateY]);

  const finishDismiss = (proceed: boolean) => {
    if (closingRef.current) {
      return;
    }

    closingRef.current = true;
    const dontShowAgain = checkedRef.current;
    Animated.parallel([
      Animated.timing(opacity, {
        toValue: 0,
        duration: 180,
        easing: Easing.in(Easing.quad),
        useNativeDriver: useNativeAnimationDriver,
      }),
      Animated.timing(translateY, {
        toValue: -8,
        duration: 180,
        easing: Easing.in(Easing.quad),
        useNativeDriver: useNativeAnimationDriver,
      }),
    ]).start(() => {
      if (proceed) {
        props.onProceed?.(dontShowAgain);
      } else {
        props.onClose?.(dontShowAgain);
      }
      setChecked(false);
      dispatch(lightBoxActions.setVisibility(false));
      dispatch(lightBoxActions.setOnClose(undefined));
      closingRef.current = false;
    });
  };

  useEffect(() => {
    dispatch(
      lightBoxActions.setOnClose(() => {
        dispatch(lightBoxActions.setOnClose(undefined));
        finishDismiss(false);
      }),
    );
  }, [dispatch, props.onClose, props.onProceed]);

  const isWeb = Platform.OS === "web";
  const hasIcon = props.icon != null;
  const isCompactAndroidLayout =
    Platform.OS === "android" && layout.width <= 380;
  const contentLeftPadding =
    props.iconContentLeftPadding ?? (hasIcon ? 76 : isWeb ? 106 : 100);
  const popupTitleFontSize =
    props.titleFontSize ?? (isCompactAndroidLayout ? 16 : 19);
  const popupTitleLineHeight = popupTitleFontSize + 2;
  const popupSubtitleFontSize = props.subtitleFontSize ?? 15;
  const goButtonMarginTop = Platform.OS === "ios" ? -6 : -3;

  return (
    <Animated.View
      style={{
        width: hasIcon
          ? Math.min(layout.width - 40, 420)
          : layout.width > 1200
            ? 520
            : Math.min(layout.width - 16, 366),
        backgroundColor: props.backgroundColor,
        borderRadius: 10,
        borderColor: props.borderColor,
        borderWidth: props.borderColor ? 1 : 0,
        shadowColor: props.shadowColor,
        shadowOffset: props.shadowColor ? { width: 0, height: 8 } : undefined,
        shadowOpacity: props.shadowColor ? 0.28 : 0,
        shadowRadius: props.shadowColor ? 18 : 0,
        elevation: props.shadowColor ? 8 : 0,
        overflow: "hidden",
        paddingTop: hasIcon ? 20 : 6,
        paddingRight: 6,
        paddingBottom: hasIcon ? 16 : 6,
        paddingLeft: 0,
        opacity,
        transform: [{ translateY }],
      }}
    >
      {props.backgroundGradientColors && (
        <LinearGradient
          colors={props.backgroundGradientColors}
          start={{ x: 0, y: 0 }}
          end={{ x: 1, y: 1 }}
          style={{ position: "absolute", top: 0, right: 0, bottom: 0, left: 0 }}
        />
      )}

      {props.backgroundDecorationColor && (
        <>
          <View
            style={{
              position: "absolute",
              width: 150,
              height: 150,
              borderRadius: 75,
              top: -90,
              right: -34,
              backgroundColor: props.backgroundDecorationColor,
            }}
          />
          <View
            style={{
              position: "absolute",
              width: 104,
              height: 104,
              borderRadius: 52,
              bottom: -76,
              left: 104,
              backgroundColor: props.backgroundDecorationColor,
            }}
          />
        </>
      )}

      <Pressable
        onPress={() => finishDismiss(false)}
        style={{ position: "absolute", top: 2, right: 10, zIndex: 3 }}
      >
        <Label style={{ color: "#ffffff", fontSize: 34, lineHeight: 34 }}>
          ×
        </Label>
      </Pressable>

      {hasIcon ? (
        <View
          style={[
            {
              position: "absolute",
              left: 18,
              top: 22,
              width: 40,
              alignItems: "center",
              justifyContent: "center",
            },
            props.iconContainerStyle,
          ]}
        >
          {props.icon}
        </View>
      ) : props.artSource ? (
        <Image
          source={props.artSource}
          style={
            [
              { position: "absolute", left: 0, zIndex: 2 },
              isWeb ? props.artWebStyle : props.artNativeStyle,
            ] as any
          }
          resizeMode={props.artResizeMode ?? "contain"}
        />
      ) : null}

      <View style={{ borderRadius: 8, overflow: "hidden" }}>
        <View
          style={{
            minHeight: props.contentMinHeight ?? 64,
            justifyContent: "center",
            paddingLeft: contentLeftPadding,
            paddingRight: hasIcon ? 30 : 106,
          }}
        >
          <Pressable onPress={() => finishDismiss(true)}>
            <Label
              style={{
                color: "#ffffff",
                fontWeight: "700",
                fontSize: popupTitleFontSize,
                lineHeight: popupTitleLineHeight,
              }}
              numberOfLines={
                hasIcon ? undefined : (props.titleNumberOfLines ?? 1)
              }
            >
              {props.title}
            </Label>
            <Label
              style={{
                color: "#ffffff",
                fontWeight: hasIcon ? "400" : "700",
                fontSize: popupSubtitleFontSize,
                marginTop: hasIcon ? 6 : 1,
                lineHeight: popupSubtitleFontSize + (hasIcon ? 6 : 2),
              }}
              numberOfLines={
                hasIcon ? undefined : (props.subtitleNumberOfLines ?? 1)
              }
            >
              {props.subtitle}
            </Label>
          </Pressable>

          <Pressable
            onPress={() => finishDismiss(true)}
            style={{
              position: hasIcon ? "relative" : "absolute",
              right: hasIcon ? undefined : 18,
              top: hasIcon ? undefined : "50%",
              alignSelf: hasIcon ? "flex-start" : undefined,
              marginTop: hasIcon ? 14 : goButtonMarginTop,
              minWidth: 72,
              height: hasIcon ? 40 : 37,
              borderRadius: 18.5,
              backgroundColor: props.buttonColor,
              borderColor: props.buttonBorderColor,
              borderWidth: props.buttonBorderColor ? 1 : 0,
              shadowColor: props.buttonShadowColor,
              shadowOffset: props.buttonShadowColor
                ? { width: 0, height: 4 }
                : undefined,
              shadowOpacity: props.buttonShadowColor ? 0.28 : 0,
              shadowRadius: props.buttonShadowColor ? 8 : 0,
              elevation: props.buttonShadowColor ? 4 : 0,
              alignItems: "center",
              justifyContent: "center",
              paddingHorizontal: 14,
            }}
          >
            <Label
              style={{
                color: props.buttonTextColor ?? "#ffffff",
                fontSize: 13,
                fontWeight: "700",
              }}
            >
              {props.ctaLabel}
            </Label>
          </Pressable>
        </View>

        <View
          style={{
            paddingLeft: contentLeftPadding,
            paddingRight: hasIcon ? 18 : 12,
            paddingBottom: 2,
            marginTop: hasIcon ? 14 : -2,
          }}
        >
          <CustomCheckbox
            value={props.checkboxValue}
            checked={checked}
            label={props.dontShowLabel}
            markerColor="#000000"
            style={{
              width: "100%",
              height: "auto",
              minHeight: 14,
              justifyContent: "flex-start",
              alignItems: "center",
              gap: 6,
            }}
            textStyle={{
              color: props.checkboxTextColor,
              fontSize: 11,
              lineHeight: 12,
              flexGrow: 1,
              flexShrink: 1,
            }}
            boxStyle={{
              borderColor: props.checkboxBorderColor,
              borderWidth: 1.5,
              backgroundColor: props.checkboxBackgroundColor,
              width: 14,
              height: 14,
              marginTop: 1,
            }}
            onChange={setChecked}
          />
        </View>
      </View>
    </Animated.View>
  );
};

export default MissionPromotionPopup;
